import { post } from 'aws-amplify/api';
import { PRESCRIPTSURE } from '../constants/api-endpoints';
import { getSessionToken } from './service-config'; // Import the helper function
const apiNamePath = PRESCRIPTSURE.STRIPE_API; // API endpoint from constants

const stripeApiService = {
  async getProductList() {
    try {
      const sessionToken = await getSessionToken();

      const productListOperation = post({
        apiName: apiNamePath,
        path: '/stripe_api',
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          queryParams: {},
          body: {
            query_path: 'get_product_list',
          },
        },
      });

      const productListResults = (
        await productListOperation.response
      ).body.json();

      return productListResults;
    } catch (error) {
      console.error('Error fetching subscription plan information:', error);
    }
  },

  async confirmCheckout(sessionId) {
    const sessionToken = await getSessionToken();
    const operation = post({
      apiName: apiNamePath,
      path: '/confirm_checkout',
      options: {
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: { session_id: sessionId },
      },
    });
    return (await operation.response).body.json();
  },

  async validatePrice(priceId) {
    const sessionToken = await getSessionToken();

    const validatePriceOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api_public',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          price_id: priceId,
          query_path: 'validate_stripe_price',
        },
      },
    });

    const validatePriceResults = (
      await validatePriceOperation.response
    ).body.json();

    return validatePriceResults;
  },

  async createCheckoutSession(priceId, email, quantity, skipTrial = false, promoCode = null) {
    try {
      const sessionToken = await getSessionToken();
      const base = window.location.origin;

      const body = {
        price_id: priceId,
        email,
        quantity,
        query_path: 'create_checkout_session',
        skip_trial: skipTrial,
        success_url: `${base}/subscription/payment/callback?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${base}/subscription`,
      };

      if (promoCode) {
        body.promo_code = promoCode;
      }

      const operation = post({
        apiName: apiNamePath,
        path: '/stripe_api',
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          body,
        },
      });

      return (await operation.response).body.json();
    } catch (error) {
      console.error('Error while creating checkout session:', error);
      throw error;
    }
  },

  async getPaymentHistory(userId, searchTerm, nextPage) {
    const sessionToken = await getSessionToken();

    const getPaymentHistoryOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          user_id: userId,
          query_path: 'transaction_history',
          search_term: searchTerm,
          next_page: nextPage,
        },
      },
    });

    const getPaymentHistoryResults = (
      await getPaymentHistoryOperation.response
    ).body.json();

    return getPaymentHistoryResults;
  },

  async getBillingDetails(subscriptionId, customerId) {
    const sessionToken = await getSessionToken();

    const getBillingDetailsOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          customer_id: customerId,
          subscription_id: subscriptionId,
          query_path: 'get_billing_details',
        },
      },
    });

    const getBillingDetailsResults = (
      await getBillingDetailsOperation.response
    ).body.json();

    return getBillingDetailsResults;
  },

  async getPaymentMethods(userId) {
    const sessionToken = await getSessionToken();

    const getPaymentMethodsOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          user_id: userId,
          query_path: 'fetch_attached_payment_methods',
        },
      },
    });

    const getPaymentMethodsResults = (
      await getPaymentMethodsOperation.response
    ).body.json();

    return getPaymentMethodsResults;
  },

  async updateSubscriptionPaymentMethod(
    userId,
    paymentMethodId,
    setAsDefault = false,
  ) {
    const sessionToken = await getSessionToken();

    const updateSubscriptionPaymentMethodOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          user_id: userId,
          payment_method_id: paymentMethodId,
          query_path: 'change_payment_method',
          is_default_payment_method: setAsDefault,
        },
      },
    });

    const updateSubscriptionPaymentMethodResults = (
      await updateSubscriptionPaymentMethodOperation.response
    ).body.json();

    return updateSubscriptionPaymentMethodResults;
  },

  async deletePaymentMethod(userId, paymentMethodId) {
    const sessionToken = await getSessionToken();

    const deletePaymentMethodOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          user_id: userId,
          payment_method_id: paymentMethodId,
          query_path: 'detach_payment_method',
        },
      },
    });

    const deletePaymentMethodResults = (
      await deletePaymentMethodOperation.response
    ).body.json();

    return deletePaymentMethodResults;
  },

  async updateBillingAddress(paymentMethodId, billingDetails) {
    const sessionToken = await getSessionToken();

    console.log('updateBillingAddress called with:', {
      paymentMethodId,
      billingDetails
    });

    // Backend expects name and address as direct properties, not nested in billing_details
    const updateBillingAddressOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          payment_method_id: paymentMethodId,
          name: billingDetails.name,
          address: billingDetails.address,
          query_path: 'update_billing_address',
        },
      },
    });

    const updateBillingAddressResults = (
      await updateBillingAddressOperation.response
    ).body.json();

    console.log('updateBillingAddress response:', updateBillingAddressResults);

    return updateBillingAddressResults;
  },

  async pauseSubscription(customerId, orgOwnerSub) {
    const sessionToken = await getSessionToken();

    const pauseSubscriptionOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          customer_id: customerId,
          org_owner_sub: orgOwnerSub,
          query_path: 'pause_subscription',
        },
      },
    });

    const pauseSubscriptionResult = (
      await pauseSubscriptionOperation.response
    ).body.json();

    return pauseSubscriptionResult;
  },

  async resumeSubscription(customerId, orgOwnerSub) {
    const sessionToken = await getSessionToken();

    const resumeSubscriptionOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          customer_id: customerId,
          query_path: 'resume_subscription',
          org_owner_sub: orgOwnerSub,
        },
      },
    });

    const resumeSubscriptionResult = (
      await resumeSubscriptionOperation.response
    ).body.json();

    return resumeSubscriptionResult;
  },

  async changeSubscriptionPlan(subscriptionId, priceId, organization, productMetadata = null) {
    const sessionToken = await getSessionToken();

    const body = {
      subscription_id: subscriptionId,
      price_id: priceId,
      org_owner_sub: organization,
      query_path: 'change_subscription_plan',
    };

    // Only include seat counts when the product is changing (plan upgrade/downgrade).
    // Frequency-only changes (ChangeFrequency.js) pass null so we don't overwrite
    // existing DynamoDB seat values with zeros.
    if (productMetadata) {
      body.seats_providers = parseInt(productMetadata.No_of_providers, 10) || 0;
      body.seats_support = parseInt(productMetadata.No_of_support_staff, 10) || 0;
    }

    const changeSubscriptionPlanOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body,
      },
    });

    const changeSubscriptionPlanResult = (
      await changeSubscriptionPlanOperation.response
    ).body.json();

    return changeSubscriptionPlanResult;
  },

  async makePaymentforSubscription(customerId) {
    const sessionToken = await getSessionToken();

    const makePaymentforSubscriptionOperation = post({
      apiName: apiNamePath,
      path: '/stripe_api',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        queryParams: {},
        body: {
          user_id: customerId,
          query_path: 'retry_failed_invoice_payment',
        },
      },
    });

    const makePaymentforSubscriptionResult = (
      await makePaymentforSubscriptionOperation.response
    ).body.json();

    return makePaymentforSubscriptionResult;
  },
};

export default stripeApiService;
